// Chart design tokens for the BOXX operator console.
//
// The CATEGORICAL series palette is the dataviz-skill reference set, re-validated
// against THIS console's surfaces (dark #0E1116, light #FAFBFC) rather than
// carried over from another product — a palette is only meaningful against the
// surface it actually renders on. Both modes pass every hard gate:
//
//   dark  — worst adjacent CVD ΔE 8.4, normal-vision 19.3, all slots >= 3:1
//   light — worst adjacent CVD ΔE 9.1, normal-vision 19.6, three slots below
//           3:1 (aqua 2.72, yellow 2.09, magenta 2.60) so the RELIEF RULE
//           applies in light mode: those series ship visible direct labels or
//           the table view. The console is dark-first, so this is the minority
//           path, but it is not dismissable.
//
// BOXX navy and gold are RESERVED for brand, chrome, and status. They are never
// a categorical series — a brand or status colour that impersonates a series is
// how a chart starts lying about which line is which.

export type Mode = "light" | "dark";

// ---------------------------------------------------------------- brand
export const BRAND = {
  navy: "#0C2344",
  navyLift: "#16345E",
  gold: "#C89614",
  goldLift: "#E0AC25",
  // WhatsApp green is a CHANNEL marker, not the BOXX brand. It means "this
  // arrived on WhatsApp" in the inbox and nothing else.
  whatsapp: "#25D366",
} as const;

// ---------------------------------------------------------------- surfaces
export const SURFACE: Record<Mode, string> = { light: "#FAFBFC", dark: "#0E1116" };

// ---------------------------------------------------------------- categorical
// 8 slots, assigned in FIXED order, never cycled. A 9th folds to "Other".
// The dark column is the same eight hues re-stepped for the dark surface, not
// a flip of the light column.
export const SERIES: Record<Mode, string[]> = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
  dark:  ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
};

// Slots that fall below 3:1 on the LIGHT surface. A chart drawing one of these
// in light mode owes the reader a direct label or the table view.
export const LIGHT_LOW_CONTRAST_SLOTS = [2, 3, 4] as const;

// Deterministic slot for a named key, so an entity keeps its colour across
// filters and re-renders. Colour follows the entity, never its rank.
const _slotCache = new Map<string, number>();
let _next = 0;
export function slotFor(key: string): number {
  if (!_slotCache.has(key)) _slotCache.set(key, _next++ % 8);
  return _slotCache.get(key)!;
}
export const seriesColor = (key: string, mode: Mode = "dark") => SERIES[mode][slotFor(key)];

// ---------------------------------------------------------------- turn stages
// The request lifecycle from the Phase 1 architecture, in execution order. Each
// stage is pinned to a fixed categorical slot so it keeps its colour in every
// chart that breaks a turn down by stage.
//
// Ten stages against eight slots: `emit` and `cachewrite` are terminal
// bookkeeping rather than latency contributors, so they reuse the slots of the
// stages they follow and are never drawn beside them.
export const STAGE_ORDER = [
  "ingress",
  "normalise",
  "disclaimer",
  "classify",
  "retrieve",
  "cache",
  "generate",
  "guardrail",
] as const;
export type Stage = (typeof STAGE_ORDER)[number];

export const STAGE_LABEL: Record<string, string> = {
  ingress: "Ingress",
  normalise: "Normalise",
  redact: "Redact",
  disclaimer: "Disclaimer",
  classify: "Classify",
  dispatch: "Dispatch",
  retrieve: "Retrieve",
  graph: "Graph",
  cache: "Cache",
  generate: "Generate",
  guardrail: "Guardrail",
  emit: "Emit",
  cachewrite: "Cache write",
  followup: "Follow-up",
  escalate: "Escalate",
  total: "Total",
};

export const stageColor = (stage: string, mode: Mode = "dark") => {
  const i = STAGE_ORDER.indexOf(stage as Stage);
  return i >= 0 ? SERIES[mode][i] : mode === "dark" ? "#8A8F98" : "#898781";
};

// ---------------------------------------------------------------- status
// Reserved. Never a categorical series. Always shipped with an icon AND a text
// label, so state is never carried by colour alone.
export const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
} as const;
export type StatusRole = keyof typeof STATUS;

// Guardrail verdicts and session states map onto the status roles rather than
// inventing their own colours.
export const VERDICT_STATUS: Record<string, StatusRole> = {
  pass: "good",
  regenerate: "warning",
  escalate: "critical",
  blocked: "critical",
  degraded: "serious",
};

// ---------------------------------------------------------------- sequential
// One hue, light -> dark, for magnitude and heatmap intensity.
export const BLUE_RAMP = [
  "#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95", "#104281",
];

export function rampAt(t: number): string {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  return BLUE_RAMP[Math.round(clamped * (BLUE_RAMP.length - 1))];
}

// Ordinal ramps (funnel stages, trust tiers) must keep the step nearest the
// surface readable, so they start inside the ramp rather than at its end.
export const ORDINAL_RAMP: Record<Mode, string[]> = {
  light: BLUE_RAMP.slice(1),
  dark: BLUE_RAMP.slice(0, -1),
};

// ---------------------------------------------------------------- diverging
// Warm/cool poles with a NEUTRAL midpoint — for signed deltas such as
// week-over-week change in scam volume. Never a hue at the midpoint.
export const DIVERGING = {
  negative: { light: "#e34948", dark: "#e66767" },
  midpoint: { light: "#f0efec", dark: "#2C3038" },
  positive: { light: "#2a78d6", dark: "#3987e5" },
} as const;

// ---------------------------------------------------------------- graph nodes
// Fraud-graph node domains. Nodes also carry a shape and a label, so this
// mapping is never the only channel.
export const NODE_DOMAIN_COLOR: Record<string, string> = {
  scam: "#e66767",
  flow: "#d95926",
  contact: "#199e70",
  portal: "#3987e5",
  organisation: "#9085e9",
  playbook: "#c98500",
  evidence: "#d55181",
  unknown: "#8A8F98",
};

// ---------------------------------------------------------------- chrome
export const INK: Record<Mode, { primary: string; secondary: string; muted: string }> = {
  light: { primary: "#0b0b0b", secondary: "#52514e", muted: "#898781" },
  dark: { primary: "#F2F4F7", secondary: "#A9B0BA", muted: "#767D88" },
};

export const CHROME: Record<Mode, { grid: string; axis: string; border: string }> = {
  light: { grid: "#e6e8eb", axis: "#c3c2b7", border: "rgba(11,11,11,0.10)" },
  dark: { grid: "#1C2027", axis: "#2C3038", border: "rgba(255,255,255,0.10)" },
};

// ---------------------------------------------------------------- mode
export function readMode(): Mode {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "dark" || attr === "light") return attr;
  // Dark-first: absent an explicit preference the console renders dark.
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}
